
    import has from 'lodash/has';
    import isEmpty from 'lodash/isEmpty';
    import dayjs from 'dayjs';
    import utc from 'dayjs/plugin/utc';
    import { mapState, mapGetters } from 'vuex';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import {
        faChevronDown as farChevronDown,
        faChevronRight as farChevronRight,
        faSparkles as farSparkles,
        faPrint as farPrint,
    } from '@fortawesome/pro-regular-svg-icons';

    import {
        faChevronDoubleRight as fasChevronDoubleRight,
        faChevronDoubleLeft as fasChevronDoubleLeft,
    } from '@fortawesome/pro-solid-svg-icons';

    import scoreHelper from '@/utils/scoreHelper';
    import { jobData } from '@/utils/jobData';
    import { slugify } from '@/utils/helpers';

    dayjs.extend(utc);

    export default {
        components: {
            FontAwesomeIcon,
        },

        data() {
            return {
                icons: {
                    farPrint,
                    fasChevronDoubleRight,
                    fasChevronDoubleLeft,
                    farChevronDown,
                    farChevronRight,
                    farSparkles,
                },
            };
        },

        computed: {
            ...mapState({
                $_locales: state => state.locales,
                $_job: state => state.job.job,
                $_scoringData: state => state.me.scoringData,
                $_scoringMatrixData: state => state.me.scoringMatrixData,
                $_brand: state => state.brand.brand,
                $_brandJobCount: state => state.brand.brand.live_job_count,
                $_me: state => state.me.user,
            }),

            ...mapGetters('job', {
                $_jobTitle: 'jobTitle',
                $_companyName: 'companyName',
                $_companyLogoUrl: 'companyLogoUrl',
                $_companySquareLogoUrl: 'companySquareLogoUrl',
                $_companyPrimaryColor: 'companyPrimaryColor',
                $_companyMedias: 'companyMedias',
                $_primaryCompanyName: 'primaryCompanyName',
                $_primaryCompanyLink: 'primaryCompanyLink',
                $_displayPrimaryBrandCrumb: 'displayPrimaryBrandCrumb',
                $_skillsDetails: 'skillsDetails',
                $_certificationsDetails: 'certificationsDetails',
                $_perksDetails: 'perksDetails',
                $_showChart: 'showChart',
                $_companyLink: 'companyLink',
                $_jobBody: 'jobBody',
                $_contractType: 'contractTypeLabel',
                $_isPrivateListing: 'isPrivateListing',
                $_occupationLabel: 'occupationLabel',
                $_categoryLabel: 'categoryLabel',
                $_industryLabel: 'industryLabel',
                $_educationLevelLabel: 'educationLevelLabel',
                $_careerLevelLabel: 'careerLevelLabel',
                $_companySize: 'companySize',
                $_locationLabel: 'locationLabel',
                $_locationLink: 'locationLink',
            }),

            ...mapGetters({
                $_currentAppUrl: 'currentAppUrl',
                $_currentLocale: 'currentLocale',
            }),

            EEOTags() {
                return has(this.$_job, 'brand_details.equal_opportunity_tags') && this.$_job.brand_details.equal_opportunity_tags.length
                    ? this.$_job.brand_details.equal_opportunity_tags
                    : null;
            },

            scoring() {
                return scoreHelper.calculateScoreFrontEnd(this.$_job, this.$_scoringData, this.$_scoringMatrixData);
            },

            similarJobsUrl() {
                return `${process.env.API_URL}/v1/match`;
            },

            matchJobsFilter() {
                return {
                    work_locations: [
                        this.$_job.location_id,
                    ],
                    skills: this.$_job.skills_details
                        ? this.$_job.skills_details.map((o) => {
                            return o.id;
                        })
                        : [],
                    occupations: [
                        {
                            occupation_id: this.$_job.occupation_id,
                            weight: '1.0000',
                        },
                    ],
                    categories: [
                        {
                            category_id: this.$_job.category_id,
                        },
                    ],
                    match_type: 'top-matches',
                    exclude_uuids: [this.$_job.uuid],
                };
            },

            $_jobData() {
                return {
                    c1_datalayer_source: 'c1',
                    seeker_match_score: this.$_job.seeker_match_score,
                    seeker_data: this.$_scoringData,
                    ...jobData(this.$_job),
                };
            },

            pageTitle() {
                let output = this.$_job.job_title;

                if (this.$_companyName) {
                    output += ` | ${this.$_companyName}`;
                }

                output += ` | ${this.$siteName}`;

                return output;
            },

            metaDescription() {
                if (this.$_job.body) {
                    return this.$_job.body.replace(/(<([^>]+)>)/ig, '').substr(0, 150);
                }

                return '';
            },

            jobviewUrl() {
                if (!this.$_job.job_title) {
                    return '';
                }

                let output = this.$_currentAppUrl;
                const locale = this.$_locales.find(o => o.country_id === this.$_job.location_level_5);
                if (locale) {
                    output = locale.app_url;
                }
                return `${output}/jobview/${slugify(this.$_job.job_title)}/${slugify(this.$_job.uuid)}`;
            },

            jobviewCanonicalUrl() {
                if (!this.$_job.job_title) {
                    return '';
                }

                return `${this.$staticRoutes.careeroneUrl}/jobview/${slugify(this.$_job.job_title)}/${slugify(this.$_job.uuid)}`;
            },

            pdfJobviewUrl() {
                if (!this.$_job.job_title) {
                    return '';
                }

                const output = this.$_currentAppUrl;
                const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                return `${output}/pdf/jobview/${slugify(this.$_job.uuid)}?timezone=${userTimeZone}`;
            },

            links() {
                return [
                    { hid: 'canonical', rel: 'canonical', href: this.jobviewCanonicalUrl },
                ];
            },

            metas() {
                const output = [];

                output.push(
                    { hid: 'description', name: 'description', content: this.metaDescription },
                    { hid: 'og:description', property: 'og:description', content: this.metaDescription },
                    { hid: 'og:url', property: 'og:url', content: this.jobviewUrl },
                    { hid: 'twitter:description', name: 'twitter:description', content: this.metaDescription },
                );

                if (this.metaDescription) {
                    output.push(
                        { hid: 'description', name: 'description', content: this.metaDescription },
                        { hid: 'og:description', property: 'og:description', content: this.metaDescription },
                        { hid: 'twitter:description', name: 'twitter:description', content: this.metaDescription },
                    );
                }

                if (this.pageTitle) {
                    output.push({ hid: 'og:title', property: 'og:title', content: this.pageTitle });
                }

                let imageUrl = '';
                const imageAlt = this.$_job.job_title;
                if (this.$_companyMedias.length) {
                    imageUrl = this.$_companyMedias[0].url;
                } else if (!isEmpty(this.$_job.company_logo_url)) {
                    imageUrl = this.$_job.company_logo_url;
                }

                if (!isEmpty(imageUrl)) {
                    output.push({ hid: 'og:image', property: 'og:image', content: this.siteBanner });
                    output.push({ hid: 'twitter:image', property: 'twitter:image', content: imageUrl });
                    output.push({ hid: 'og:image:alt', property: 'og:image:alt', content: imageAlt });
                }
                output.push({ hid: 'robots', name: 'robots', content: (this.$siteName === 'CareerOne') ? 'noindex, follow' : 'index' });
                output.push({ hid: 'robots2', name: 'robots', content: `unavailable_after: ${dayjs.utc((this.$_job.expires_at || dayjs.utc(this.$_job.activates_at).add(30, 'day'))).local().format('YYYY-MM-DD')}` });
                return output;
            },

            script() {
                const output = [];
                const innerHTML = {
                    '@context': 'http://schema.org',
                    '@type': 'JobPosting',
                    datePosted: dayjs.utc(this.$_job.activates_at).local().format('YYYY-MM-DD'),
                    baseSalary: {
                        '@type': 'MonetaryAmount',
                        currency: 'AUD',
                        minValue: this.$_job.pay_min,
                        maxValue: this.$_job.pay_max,
                    },
                    jobBenefits: this.$_perksDetails.map(o => o.value).join(', '),
                    description: this.$_job.job_description,
                    industry: this.$_industryLabel,
                    jobLocation: {
                        '@type': 'Place',
                        address: {
                            '@type': 'PostalAddress',
                            streetAddress: 'N/A',
                            addressLocality: this.$_job.location_level_3_label ? this.$_job.location_level_3_label : 'N/A',
                            addressRegion: this.$_job.location_state_code ? this.$_job.location_state_code : 'N/A',
                            postalCode: this.$_job.location_post_code ? this.$_job.location_post_code : 'N/A',
                            addressCountry: this.$_job.location_country_code ? this.$_job.location_country_code : 'N/A',
                        },
                    },
                    hiringOrganization: {
                        '@type': 'Organization',
                        name: this.$_isPrivateListing ? 'Private Listing' : this.$_companyName ? this.$_companyName : 'CareerOne Job Listing',
                        logo: this.$_companyLogoUrl ? this.$_companyLogoUrl : 'https://www.careerone.com.au/images/careerone_banner.png',
                    },
                    occupationalCategory: `${this.$_occupationLabel} ${this.$_categoryLabel}`,
                    skills: this.$_skillsDetails.map(o => o.value).join(', '),
                    title: this.$_jobTitle,
                    url: `${this.$siteUrl}${this.$route.fullPath.replace('/', '')}`,
                    validThrough: dayjs.utc(this.$_job.expires_at).local().format('YYYY-MM-DD'),
                };

                if (this.$_educationLevelLabel) {
                    innerHTML.educationRequirements = {
                        '@type': 'EducationalOccupationalCredential',
                        credentialCategory: this.$_educationLevelLabel ? this.$_educationLevelLabel : 'N/A',
                    };
                }
                if (this.$_contractType) {
                    innerHTML.employmentType = this.$_contractType;
                }
                output.push({
                    type: 'application/ld+json',
                    innerHTML: JSON.stringify(innerHTML),
                });

                return output;
            },
        },

        methods: {
            getCategoryLink() {
                const routePath = this.$route.path;
                let categoryLink = `/jobs-in-${this.$_job.category_slug}`;
                if (this.$_job.location_level_3_slug && !routePath.includes('/in-')) {
                    const locationLink = `in-${this.$_job.location_level_3_slug}`;
                    categoryLink = `${categoryLink}/${locationLink}`;
                }
                return categoryLink;
            },

            getOccupationLink() {
                const routePath = this.$route.path;
                let occupationLink = '/jobs';
                if (this.$_job.location_level_3_slug && !routePath.includes('/in-')) {
                    const locationLink = `in-${this.$_job.location_level_3_slug}`;
                    occupationLink = `${occupationLink}/${locationLink}`;
                }
                occupationLink = `${occupationLink}/occ_${this.$_job.occupation_slug}`;
                return occupationLink;
            },
        },
    };
